import type {
  AgentBuilderHistory,
  AgentGraphOutputs,
  AgentModelConfig,
  AgentNewNodeKind,
  AgentNodeManaged,
  AgentSuggestionOp,
  AgentTool,
  AgentToolBuiltin,
  AgentToolMcp,
  AgentToolVaultProxy,
  AgentToolVendor,
  ManagedTemplate,
  MimeType,
  NodeValidation,
  SkillFileNode,
} from '@onefootprint/request-types/dashboard';

export type FieldType =
  | 'text'
  | 'document'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'enum'
  | 'object'
  | 'array<string>'
  | 'array<enum>'
  | 'array<object>';

export type Property = {
  id: string;
  name: string;
  type: FieldType;
  description?: string;
  enumValues?: string[];
  /** Keyed by value, like {@link Field.criteria}: a nested enum describes its values the same way a field does. */
  criteria?: Record<string, string>;
  children?: Property[];
};

export type Field = {
  id: string;
  name: string;
  type: FieldType;
  unit?: string;
  enumValues?: string[];
  /**
   * When the agent should choose each allowed value, keyed by the value itself, so renaming a value
   * has to carry its entry across (the schema editor does). Rides the wire under a provisional
   * vendor key — see `SchemaWithExtras`.
   */
  criteria?: Record<string, string>;
  prompt: string;
  children?: Property[];
};

export type ArtifactType = Extract<MimeType, 'markdown' | 'pdf' | 'csv'>;

export type Artifact = {
  id: string;
  name: string;
  type: ArtifactType;
  spec: string;
  isStarred: boolean;
};

export type Task = {
  kind: 'task';
  id: string;
  name: string;
  prompt: string;
  fields: Field[];
  artifacts: Artifact[];
};

export type RuleCondition = {
  id: string;
  expression: string;
};

export type RuleGroup = {
  id: string;
  value: string;
  conditions: RuleCondition[];
};

export type RulesNode = {
  kind: 'rules';
  id: string;
  name: string;
  key: string;
  defaultValue: string;
  groups: RuleGroup[];
};

export type DecisionStateRef = {
  id: string;
  source: 'input' | 'output';
  key: string;
};

export type DecisionOption = {
  id: string;
  value: string;
  description: string;
};

// Probabilities stay strings while editing so a half-typed "0." survives a re-render; the wire
// layer parses them.
export type DecisionQuestionYesNo = {
  id: string;
  kind: 'yes_no';
  key: string;
  prompt: string;
  yesMeans: string;
  noMeans: string;
  threshold: string;
};

export type DecisionQuestionPickOne = {
  id: string;
  kind: 'pick_one';
  key: string;
  prompt: string;
  options: DecisionOption[];
};

export type DecisionLevel = {
  id: string;
  description: string;
};

// Emits a number from 0 to levels.length - 1: the closest level, weighted by probability.
export type DecisionQuestionScore = {
  id: string;
  kind: 'score';
  key: string;
  prompt: string;
  levels: DecisionLevel[];
};

export type DecisionQuestion = DecisionQuestionYesNo | DecisionQuestionPickOne | DecisionQuestionScore;

export type DecisionNode = {
  kind: 'decision';
  id: string;
  name: string;
  context: string;
  state: DecisionStateRef[];
  questions: DecisionQuestion[];
  model: string;
};

// `task` (outputs) and `template` aren't persisted on the node — they're server-computed and
// joined from the response's nodeContext each load (see bundleFromResponse).
export type ManagedNodeKind = Exclude<AgentNewNodeKind, 'task' | 'rules' | 'decision'>;

export type ManagedNode = {
  kind: 'managed';
  id: string;
  managedKind: ManagedNodeKind;
  config: Record<string, unknown>;
  // Pinned template version; the server sets it on add and the FE round-trips it unchanged.
  version: AgentNodeManaged['data']['version'];
  task: Task;
  template: ManagedTemplate;
};

export type BundleNode = Task | RulesNode | ManagedNode | DecisionNode;

export const isTaskNode = (n: BundleNode): n is Task => n.kind === 'task';
export const isRulesNode = (n: BundleNode): n is RulesNode => n.kind === 'rules';
export const isManagedNode = (n: BundleNode): n is ManagedNode => n.kind === 'managed';
export const isDecisionNode = (n: BundleNode): n is DecisionNode => n.kind === 'decision';
export const isVendorTool = (tool: AgentTool): tool is AgentToolVendor => tool.kind === 'vendor';
export const isMcpTool = (tool: AgentTool): tool is AgentToolMcp => tool.kind === 'mcp';
export const isBuiltinTool = (tool: AgentTool): tool is AgentToolBuiltin => tool.kind === 'builtin';
export const isVaultProxyTool = (tool: AgentTool): tool is AgentToolVaultProxy => tool.kind === 'vault_proxy';

// A managed node's label lives on the task it lowers to, so callers that just want something to
// display don't have to special-case the kind.
export const nodeName = (n: BundleNode): string => (isManagedNode(n) ? n.task.name : n.name);

// A field carries child properties when it's an object (or a list of objects),
// and enum values when it's an enum (or a list of enums).
export const isObjectType = (type: FieldType | null): boolean => type === 'object' || type === 'array<object>';
export const isEnumType = (type: FieldType | null): boolean => type === 'enum' || type === 'array<enum>';

export type InputField = Pick<Field, 'enumValues' | 'children'> & {
  id: string;
  name: string;
  description: string;
  // A null type is a legacy input with no schema; it round-trips unchanged.
  type: FieldType | null;
  // Governs presence only. An optional input may be omitted, but a value it does receive is
  // still held to `type`.
  required: boolean;
};

export type Suggestion = {
  id: string;
  canApply: boolean;
  operation: AgentSuggestionOp;
  /** Short, human-readable summary Percy authored for this suggestion. */
  description?: string;
};

export type Bundle = {
  name: string;
  icon?: string;
  norms: string;
  inputs: InputField[];
  includeAllVaultDocuments?: boolean;
  nodes: BundleNode[];
  skills: SkillFileNode[];
  tools: AgentTool[];
  /** Internal-only model routing; only present for risk-ops actors (gated server-side). */
  modelConfig?: AgentModelConfig;
  outputs?: AgentGraphOutputs;
  suggestions: Suggestion[];
};

export type SessionMeta = {
  agentId: string;
  agentVersionId: string;
  /** Set when the head is itself a published version — re-publishing it is rejected by the backend. */
  versionTag?: string;
};

export type Validations = NodeValidation[];

export const EMPTY_HISTORY: AgentBuilderHistory = {};

export type ViewMode = 'plan' | 'tools' | 'mcp' | 'vaultProxies' | 'norms' | 'skills' | 'credentials';

// Synthetic ids so the inputs and final-output sections can carry validation chips and nav
// rows even though neither is a real graph node on the wire.
export const INPUT_NODE_ID = 'input';
export const OUTPUTS_NODE_ID = 'outputs';
